import React, { lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
// Minimal coordinate presence - observatory grid
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-603" />;
export function Decode() {
  const productStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Decoder',
    description: 'A pattern recognition engine for human systems. Reveals structure, loops, constraints, and patterns.',
    brand: {
      '@type': 'Brand',
      name: 'Lazy Monk Labs'
    }
  };
  return <>
      <Helmet data-id="element-604">
        <title data-id="element-605">Decoder — Pattern Recognition Engine | Lazy Monk Labs</title>
        <meta name="description" content="Decoder is a pattern recognition engine for human systems. It does not give advice. It reveals structure." data-id="element-606" />
        <meta name="robots" content="index, follow" data-id="element-607" />
        <link rel="canonical" href="https://lazymonklabs.com/decoder" data-id="element-608" />

        <script type="application/ld+json" data-id="element-609">
          {JSON.stringify(productStructuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-610">
        {/* Navigation */}
        <header data-id="element-611">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-612">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-613">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-614">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-615" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-616">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-617">
                <Link to="/diagnostics" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-medium" data-id="element-618">
                  Diagnostics
                </Link>
                <Link to="/work-mirror" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-medium" data-id="element-619">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="text-sm text-[#F5F5F5] hover:text-gray-400 transition-colors font-mono" data-id="element-620">
                  Decoder
                </Link>
                <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-621">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-622">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16" data-id="element-623">
          {/* 1. HERO */}
          <section className="relative py-32 md:py-40 px-6 overflow-hidden" data-id="element-624">
            <CoordinateGrid data-id="element-625" />
            <div className="relative z-10 max-w-4xl mx-auto space-y-12" data-id="element-626">
              <div className="space-y-6" data-id="element-627">
                <h1 className="text-5xl md:text-7xl font-serif leading-none text-[#F5F5F5]" style={{
                fontFamily: 'Georgia, serif'
              }} data-id="element-628">
                  Decoder is a pattern recognition engine.
                </h1>

                <p className="text-xl md:text-2xl text-gray-400 font-light leading-relaxed" data-id="element-629">
                  It detects structure in how humans think, decide, and act — by
                  analyzing how they respond to situational questions.
                </p>
              </div>

              <div className="pt-6" data-id="element-630">
                <p className="text-sm text-gray-600" data-id="element-631">
                  Not content. Not templated advice. A system detecting
                  structure.
                </p>
              </div>
            </div>
          </section>

          {/* 2. WHAT IT IS (AND IS NOT) */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-632">
            <div className="max-w-4xl mx-auto" data-id="element-633">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-634">
                What Decoder is
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-635">
                <p data-id="element-636">
                  Decoder is not psychology.
                  <br data-id="element-637" />
                  Not coaching.
                  <br data-id="element-638" />
                  Not self-help.
                </p>

                <p data-id="element-639">
                  It is a system designed to detect repeating structural
                  patterns in human behavior.
                </p>

                <p className="pt-6 border-t border-[#2B2B2B]" data-id="element-640">
                  It does not work with stories or emotions.
                  <br data-id="element-641" />
                  It works with structure, tradeoffs, and loops.
                </p>
              </div>
            </div>
          </section>

          {/* 3. HOW IT WORKS (HIGH LEVEL) */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-642">
            <div className="max-w-4xl mx-auto" data-id="element-643">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-644">
                How it works
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-645">
                <p data-id="element-646">
                  Decoder analyzes how a person answers carefully designed
                  situational questions.
                </p>

                <div className="pl-6 border-l-2 border-[#2B2B2B] space-y-4" data-id="element-647">
                  <p className="text-gray-500" data-id="element-648">
                    From these answers, it infers:
                  </p>
                  <p data-id="element-649">• The structure they tend to operate from</p>
                  <p data-id="element-650">• The tradeoffs this structure creates</p>
                  <p data-id="element-651">• The loop that repeats under pressure</p>
                </div>

                <p className="pt-6 border-t border-[#2B2B2B] text-[#F5F5F5]" data-id="element-652">
                  It does not predict.
                  <br data-id="element-653" />
                  It does not advise.
                  <br data-id="element-654" />
                  It makes structure visible.
                </p>
              </div>
            </div>
          </section>

          {/* 4. WHAT DECODER POWERS */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-655">
            <div className="max-w-6xl mx-auto" data-id="element-656">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-16" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-657">
                Products powered by Decoder
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-658">
                {/* DIAGNOSTICS */}
                <div className="border border-[#2B2B2B] p-10 space-y-8 bg-[#0A0A0A]" data-id="element-659">
                  <div className="space-y-4" data-id="element-660">
                    <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-661">
                      Diagnostics
                    </h3>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-662">
                      One-time, deep pattern reports.
                      <br data-id="element-663" />
                      <br data-id="element-664" />
                      They give you a precise snapshot of your operating
                      structure in a specific domain.
                    </p>
                  </div>

                  <div className="space-y-3 text-sm text-gray-500" data-id="element-665">
                    <p data-id="element-666">• Self Diagnostic</p>
                    <p data-id="element-667">• Work Diagnostic</p>
                    <p data-id="element-668">• Relationship Diagnostic</p>
                    <p data-id="element-669">• Child Diagnostic</p>
                  </div>

                  <div className="pt-4" data-id="element-670">
                    <Link to="/diagnostics" data-id="element-671">
                      <Button className="bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-6 py-4 text-sm font-medium w-full" data-id="element-672">
                        Explore Diagnostics
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* WORK MIRROR */}
                <div className="border border-[#2B2B2B] p-10 space-y-8 bg-[#0A0A0A]" data-id="element-673">
                  <div className="space-y-4" data-id="element-674">
                    <h3 className="text-2xl font-medium text-[#F5F5F5]" data-id="element-675">
                      Work Mirror
                    </h3>
                    <p className="text-base text-gray-400 leading-relaxed" data-id="element-676">
                      A living system that uses Decoder continuously to reflect
                      how your work pattern shows up in real situations over
                      time.
                    </p>
                  </div>

                  <div className="space-y-3 text-sm text-gray-500" data-id="element-677">
                    <p data-id="element-678">• Log situations, modes, and days</p>
                    <p data-id="element-679">• See repeating loops</p>
                    <p data-id="element-680">• Build a long-term pattern record</p>
                  </div>

                  <div className="pt-4" data-id="element-681">
                    <Link to="/work-mirror" data-id="element-682">
                      <Button className="bg-transparent text-[#F5F5F5] hover:bg-[#F5F5F5]/10 border border-[#2B2B2B] px-6 py-4 text-sm font-medium w-full" data-id="element-683">
                        Explore Work Mirror
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 5. SNAPSHOTS VS LIVING SYSTEMS */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-684">
            <div className="max-w-4xl mx-auto" data-id="element-685">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-12" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-686">
                Two time horizons
              </h2>

              <div className="space-y-8 text-lg text-gray-400 leading-relaxed" data-id="element-687">
                <p data-id="element-688">The same engine is applied in two ways:</p>

                <div className="pl-6 border-l-2 border-[#2B2B2B] space-y-4" data-id="element-689">
                  <p data-id="element-690">Diagnostics use Decoder to generate snapshots.</p>
                  <p data-id="element-691">
                    Work Mirror uses Decoder to observe the same structure in
                    motion over time.
                  </p>
                </div>

                <p className="pt-6 border-t border-[#2B2B2B] text-[#F5F5F5]" data-id="element-692">
                  Same system.
                  <br data-id="element-693" />
                  Different time horizons.
                </p>
              </div>
            </div>
          </section>

          {/* 6. WHAT THIS IS NOT */}
          <section className="py-24 px-6 border-t border-[#2B2B2B]" data-id="element-694">
            <div className="max-w-6xl mx-auto" data-id="element-695">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5] mb-16 text-center" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-696">
                This is not self-help.
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-697">
                <div className="space-y-4 text-center" data-id="element-698">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-699">
                    Not Therapy
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-700">
                    Not about emotions or stories.
                  </p>
                </div>

                <div className="space-y-4 text-center" data-id="element-701">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-702">
                    Not Productivity
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-703">
                    Not about optimizing output.
                  </p>
                </div>

                <div className="space-y-4 text-center" data-id="element-704">
                  <h3 className="text-xl font-medium text-[#F5F5F5]" data-id="element-705">
                    Not Advice
                  </h3>
                  <p className="text-base text-gray-400 leading-relaxed" data-id="element-706">
                    Not about telling you what to do.
                  </p>
                </div>
              </div>

              <div className="pt-12 text-center" data-id="element-707">
                <p className="text-lg text-[#F5F5F5] leading-relaxed" data-id="element-708">
                  Decoder does not change people. It makes patterns visible.
                </p>
              </div>
            </div>
          </section>

          {/* 7. FINAL GATEWAY */}
          <section className="py-24 px-6 bg-[#111214] border-t border-[#2B2B2B]" data-id="element-709">
            <div className="max-w-4xl mx-auto text-center space-y-12" data-id="element-710">
              <h2 className="text-3xl md:text-4xl font-serif text-[#F5F5F5]" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-711">
                Choose how you want to use the system
              </h2>

              <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-2xl mx-auto" data-id="element-712">
                <Link to="/diagnostics" className="flex-1" data-id="element-713">
                  <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-10 py-5 text-sm font-medium w-full" data-id="element-714">
                    Go to Diagnostics
                  </Button>
                </Link>
                <Link to="/work-mirror" className="flex-1" data-id="element-715">
                  <Button className="bg-[#F5F5F5] text-black hover:bg-gray-200 border-none px-10 py-5 text-sm font-medium w-full" data-id="element-716">
                    Go to Work Mirror
                  </Button>
                </Link>
              </div>

              <div className="pt-4" data-id="element-717">
                <p className="text-sm text-gray-600" data-id="element-718">
                  Both are powered by the same engine.
                </p>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-719">
          <div className="max-w-6xl mx-auto" data-id="element-720">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-721">
              <div data-id="element-722">
                <p className="text-sm text-gray-500 font-mono" data-id="element-723">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-724">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-725">
                <Link to="/diagnostics" className="hover:text-[#F5F5F5] transition-colors" data-id="element-726">
                  Diagnostics
                </Link>
                <Link to="/work-mirror" className="hover:text-[#F5F5F5] transition-colors" data-id="element-727">
                  Work Mirror
                </Link>
                <Link to="/decoder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-728">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-729">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-730">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-731">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}